(ns io.daskul.tutorial.webapp.components.invoice-lines
  "Invoice detail line-item panel (#843 slice 6). Renders the SAME shared
   line-editor the New Invoice page uses — the anti-drift contract — over LOCAL
   editor state. An invoice is financial, so persistence is a deliberate act: all
   callbacks (on-add / on-edit / on-remove / on-include) mutate local state only,
   and Save commits the batch diff against the last-saved rows in one shot —
   POST new lines, PATCH changed ones, DELETE dropped ones. Cancel discards the
   edits back to the saved rows. (The invoice header form keeps its own Save.)

   Registered as a :custom-child-panels component for [:invoice
   :invoice-line-item], so it replaces the standard child table entirely."
  (:require
   [uix.core :as uix :refer [defui $]]
   [re-frame.core :as rf]
   [uix.re-frame :as urf]
   [io.daskul.webapp.api.http :as api]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.format :as format]
   [io.daskul.webapp.notifications :as notif]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.widgets.billing.line-editor :as le]
   [io.daskul.webapp.widgets.billing.line-editor-logic :as le-logic]))

;; Toast on a successful line save (the panel saves via api/request!, outside the
;; re-frame event flow, so it hops through this event to reach the :notification fx).
(rf/reg-event-fx ::notify-saved
  (fn [_ [_ message]] {:notification (notif/success "Saved" message)}))

(defn- fmt-money
  "Currency with a leading minus for negatives (credits): −₱1,000.00."
  [n]
  (if (neg? n) (str "−" (format/format-currency (- n))) (format/format-currency n)))

;; ---------------------------------------------------------------------------
;; Persisted line-item ↔ editor line
;; ---------------------------------------------------------------------------

(defn- item->line
  "Map a persisted line-item row to an editor line, keyed by its DB id and with
   :kind derived (source ⇒ enrollment, negative unit-price ⇒ credit, else charge)."
  [item]
  (let [kind (cond (:source-type item) :enrollment
                   (neg? (or (:unit-price item) 0)) :credit
                   :else :charge)]
    (assoc (select-keys item le-logic/item-keys)
           :line-id (str (:id item))
           :db-id (:id item)
           :kind kind
           :beneficiary-name (:beneficiary-name item))))

(defn- source-key [x] [(str (:source-type x)) (str (:source-id x))])

(defn- available-from
  "Enrollment billables the customer has that are not already on the invoice."
  [panels discounts on-invoice-sources]
  (-> (le-logic/billables->lines panels)
      (le-logic/apply-auto-discounts discounts)
      (->> (remove #(contains? on-invoice-sources (source-key %)))
           vec)))

;; ---------------------------------------------------------------------------
;; Deliberate save — edits stay local until Save, which PUTs the whole line set
;; and lets the server reconcile it (an invoice is financial, so persistence is
;; an explicit act, not a keystroke side-effect). The client stays dumb: it sends
;; the desired lines, the server owns "what changed".
;; ---------------------------------------------------------------------------

(defn- req-promise
  "api/request! as a Promise, so the save can be awaited."
  [opts]
  (js/Promise. (fn [resolve reject]
                 (api/request! (assoc opts :on-success resolve :on-error reject)))))

(defn- persist-view
  "A line projected to the save payload: the create shape plus :id on a line that
   already exists (the server reconciles on it; new lines carry none)."
  [line]
  (cond-> (le-logic/line->item line)
    (:db-id line) (assoc :id (:db-id line))))

(defn- lines-changed?
  "Whether the editor's lines differ from the last-saved set — drives Save/Cancel.
   Compares the save projection as a set, so add / remove / any field edit counts."
  [baseline current]
  (not= (set (map persist-view baseline))
        (set (map persist-view current))))

(defn- run-save!
  "Persist the whole line set in ONE request (PUT the collection) → one server
   transaction; the server reconciles by id, so the save is atomic. New lines
   carry no :id/:invoice-id — the endpoint stamps invoice-id from the path."
  [invoice-id lines]
  (req-promise {:method :put
                :path (str "/billing/invoices/" invoice-id "/line-items")
                :params {:lines (mapv persist-view lines)}}))

;; ---------------------------------------------------------------------------
;; Panel
;; ---------------------------------------------------------------------------

(defui invoice-lines-panel
  [{:keys [parent-agg-key parent-id child-agg-key fk-field parent-record]}]
  (let [draft? (= "draft" (:state parent-record))
        customer-type (:customer-type parent-record)
        customer-id (some-> (:customer-id parent-record) str)

        persisted (urf/use-subscribe [::agg.subs/child-items parent-agg-key parent-id child-agg-key])
        ;; `child-items` is [] both before the fetch and when genuinely empty;
        ;; child-loaded? tells them apart so we seed the editor only once the
        ;; line items have actually arrived.
        loaded? (urf/use-subscribe [::agg.subs/child-loaded? parent-agg-key parent-id child-agg-key])

        [editor set-editor!] (uix/use-state {:lines [] :available []})
        [discounts set-discounts!] (uix/use-state [])
        [billables set-billables!] (uix/use-state nil) ; {:panels :discounts} once fetched
        [saving? set-saving!] (uix/use-state false)
        [error set-error!] (uix/use-state nil)
        [attempted? set-attempted!] (uix/use-state false) ; surface required-field errors after a Save attempt
        initialized? (uix/use-ref false)
        seq-ref (uix/use-ref 0) ; local ids for not-yet-persisted lines
        {:keys [lines available]} editor

        ;; Seed the editor from the last-saved rows + the customer's billables.
        ;; Re-run on save (persisted refreshes) and on Cancel (discard edits).
        do-seed
        (uix/use-callback
         (fn []
           (let [ds (:discounts billables)
                 seeded (mapv item->line persisted)
                 on-inv (into #{} (comp (filter :source-type) (map source-key)) seeded)]
             (set-discounts! ds)
             (set-editor! {:lines seeded
                           :available (available-from (:panels billables) ds on-inv)})))
         [billables persisted])

        ;; All edits mutate LOCAL state only — nothing persists until Save.
        add-line!
        (fn [kind]
          (let [n (inc @seq-ref)]
            (reset! seq-ref n)
            (set-editor! (fn [st]
                           ;; unit-price left blank on purpose — it's required, so
                           ;; the field starts empty rather than a stand-in 0.
                           (update st :lines conj
                                   {:line-id (str (name kind) ":" n) :kind kind
                                    :description "" :quantity 1})))))

        edit-line!
        (fn [line-id patch]
          (set-editor! (fn [st] (update st :lines
                                        (fn [ls] (mapv #(if (= (:line-id %) line-id) (merge % patch) %) ls))))))

        remove-line!
        (fn [line-id] (set-editor! (fn [st] (le-logic/exclude-line st line-id))))

        include-line!
        (fn [line-id] (set-editor! (fn [st] (le-logic/include-line st line-id))))

        seeded? @initialized?
        changed? (and seeded? (lines-changed? (mapv item->line persisted) lines))
        invalid (le-logic/lines-invalid lines)

        save!
        (fn []
          (if (seq invalid)
            ;; Block the save and surface the empty required fields in red.
            (set-attempted! true)
            (do
              (set-attempted! false)
              (set-saving! true)
              (set-error! nil)
              (-> (run-save! parent-id lines)
                  (.then (fn [_]
                           (set-saving! false)
                           ;; refresh totals + rows; reset the guard so do-seed
                           ;; re-seeds from the refreshed rows (with new db-ids).
                           (reset! initialized? false)
                           (rf/dispatch [::agg.events/get parent-agg-key parent-id])
                           (rf/dispatch [::agg.events/list-children parent-agg-key parent-id child-agg-key fk-field])
                           (rf/dispatch [::notify-saved "Line items saved."])))
                  (.catch (fn [_] (set-saving! false) (set-error! "Failed to save lines")))))))

        cancel! (fn [] (set-error! nil) (set-attempted! false) (do-seed))]

    ;; Load persisted items on mount, and fetch the customer's billables.
    (uix/use-effect
     (fn []
       (rf/dispatch [::agg.events/list-children parent-agg-key parent-id child-agg-key fk-field])
       (if (and customer-type customer-id)
         (api/request!
          {:method :get
           :path "/billing/billables"
           :params {:customer-type customer-type :customer-id customer-id}
           :on-success (fn [resp] (set-billables! {:panels (or (:panels resp) [])
                                                   :discounts (or (:discounts resp) [])}))
           :on-error (fn [_] (set-billables! {:panels [] :discounts []}))})
         (set-billables! {:panels [] :discounts []}))
       js/undefined)
     [parent-agg-key parent-id child-agg-key fk-field customer-type customer-id])

    ;; Seed once billables + persisted are both in (do-seed changes when either
    ;; does). The initialized? guard keeps a background refresh from clobbering
    ;; unsaved edits; Save clears it deliberately to pull in the saved rows.
    (uix/use-effect
     (fn []
       (when (and billables loaded? (not @initialized?))
         (do-seed)
         (reset! initialized? true))
       js/undefined)
     [billables loaded? do-seed])

    ($ ui/card {:shadow "sm" :padding #js {:base "sm" :sm "lg"} :radius "md" :withBorder true}
      ($ ui/stack {:gap "md"}
        (when error ($ ui/error-alert error))
        (cond
          (not draft?)
          ;; Issued → read-only, but the SAME columns as the editor so it reads as
          ;; the same invoice, just locked (not a stripped-down list).
          ($ :<>
            ($ ui/info-alert {} "This invoice is issued — line items are locked.")
            ($ ui/table {}
              ($ ui/thead
                ($ ui/tr
                  ($ ui/th "Description")
                  ($ ui/th {:style {:width 88}} "Qty")
                  ($ ui/th {:style {:width 140}} "Unit price")
                  ($ ui/th {:style {:textAlign "right"}} "Amount")))
              ($ ui/tbody
                (for [l (mapv item->line persisted)
                      :let [credit? (le-logic/credit? l)
                            disc (:discount-amount l)]]
                  ($ ui/tr {:key (:line-id l)}
                    ($ ui/td
                      ($ ui/stack {:gap 2}
                        ($ ui/text (or (not-empty (:description l))
                                       (if credit? "Credit" "—")))
                        (when (:beneficiary-name l)
                          ($ le/beneficiary-pill {:name (:beneficiary-name l)}))
                        (when (and disc (pos? disc))
                          ($ ui/text {:size "xs" :c "green"}
                            (str "Discount −" (format/format-currency disc))))))
                    ($ ui/td (:quantity l))
                    ($ ui/td (fmt-money (:unit-price l)))
                    ($ ui/td {:style {:textAlign "right"}}
                      ($ ui/text (cond-> {} credit? (assoc :c "green"))
                        (fmt-money (le-logic/line-subtotal l)))))))))

          (not (and billables loaded?))
          ($ ui/center {:py "xl"} ($ ui/loader {:size "sm"}))

          :else
          ($ :<>
            ($ le/line-editor
              {:lines lines
               :available available
               :discounts discounts
               :invalid (when attempted? invalid)
               :on-edit edit-line!
               :on-remove remove-line!
               :on-include include-line!
               :on-add add-line!})
            ;; Deliberate save: line edits are staged locally and committed here.
            ($ ui/group {:justify "flex-end" :gap "sm"}
              ($ ui/button {:variant "default" :onClick cancel!
                            :disabled (or (not changed?) saving?)} "Cancel")
              ($ ui/button {:onClick save! :loading saving? :disabled (not changed?)}
                "Save lines"))))))))
